import React, { useState, useEffect } from 'react';
import { Button, Input, Textarea, Modal, EmptyState } from '../../components';
import * as phaseService from '../../services/phase-service';
import * as courseService from '../../services/course-service';

interface CourseOption {
  _id: string;
  title: string;
  category: string;
}

interface Phase {
  id: string;
  order: number;
  title: string;
  description: string;
  courseIds: string[];
}

const PhaseConfig: React.FC = () => {
  const [phases, setPhases] = useState<Phase[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableCourses, setAvailableCourses] = useState<CourseOption[]>([]);

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState<Phase | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSelectedCourseIds, setEditSelectedCourseIds] = useState<string[]>([]);
  const [editCoursePickerOpen, setEditCoursePickerOpen] = useState(false);

  // Create modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createSelectedCourseIds, setCreateSelectedCourseIds] = useState<string[]>([]);
  const [createCoursePickerOpen, setCreateCoursePickerOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [phasesData, coursesData] = await Promise.all([
          phaseService.getPhases(),
          courseService.getCourses(),
        ]);
        setPhases(phasesData.map((p: any) => ({
          id: p._id,
          order: p.order,
          title: p.title,
          description: p.description,
          courseIds: p.courseIds || [],
        })));
        setAvailableCourses(
          coursesData
            .filter((c: any) => c.status === 'published')
            .map((c: any) => ({ _id: c._id, title: c.title, category: c.category }))
            .sort((a: CourseOption, b: CourseOption) => a.title.localeCompare(b.title))
        );
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ── Edit ──────────────────────────────────────────────────────────────
  const openEditModal = (phase: Phase) => {
    setEditingPhase(phase);
    setEditTitle(phase.title);
    setEditDescription(phase.description);
    setEditSelectedCourseIds(phase.courseIds || []);
    setEditCoursePickerOpen(false);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingPhase(null);
  };

  const toggleEditCourse = (id: string) =>
    setEditSelectedCourseIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const handleSavePhaseEdit = async () => {
    if (!editingPhase) return;
    try {
      await phaseService.updatePhase(editingPhase.id, {
        title: editTitle,
        description: editDescription,
        courseIds: editSelectedCourseIds,
      });
      setPhases(prev =>
        prev.map(p =>
          p.id === editingPhase.id
            ? { ...p, title: editTitle, description: editDescription, courseIds: editSelectedCourseIds }
            : p
        )
      );
    } catch (e) { console.error(e); }
    handleCloseEditModal();
  };

  // ── Create ────────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setCreateTitle('');
    setCreateDescription('');
    setCreateSelectedCourseIds([]);
    setCreateCoursePickerOpen(false);
    setCreateModalOpen(true);
  };

  const toggleCreateCourse = (id: string) =>
    setCreateSelectedCourseIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const handleCreatePhase = async () => {
    if (!createTitle.trim()) return;
    setCreating(true);
    try {
      const created = await phaseService.createPhase({
        title: createTitle.trim(),
        description: createDescription,
        courseIds: createSelectedCourseIds,
      });
      setPhases(prev => [...prev, {
        id: created._id,
        order: created.order,
        title: created.title,
        description: created.description,
        courseIds: created.courseIds || [],
      }]);
      setCreateModalOpen(false);
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  // ── Delete ────────────────────────────────────────────────────────────
  const handleDeletePhase = async (id: string) => {
    if (!window.confirm('Delete this phase? This cannot be undone.')) return;
    try {
      await phaseService.deletePhase(id);
      setPhases(prev => prev.filter(p => p.id !== id));
    } catch (e) { console.error(e); }
  };

  // ── Reorder ───────────────────────────────────────────────────────────
  const movePhase = (index: number, direction: 'up' | 'down') => {
    const newPhases = [...phases];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newPhases.length) return;
    [newPhases[index], newPhases[targetIndex]] = [newPhases[targetIndex], newPhases[index]];
    newPhases.forEach((p, i) => { p.order = i + 1; });
    setPhases(newPhases);
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      await phaseService.reorderPhases(phases.map(p => ({ id: p.id, order: p.order })));
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  // ── Course picker shared UI ───────────────────────────────────────────
  const CoursePicker = ({
    selectedIds, onToggle, open, onToggleOpen, excludePhaseId,
  }: {
    selectedIds: string[];
    onToggle: (id: string) => void;
    open: boolean;
    onToggleOpen: () => void;
    excludePhaseId?: string;
  }) => {
    // Courses claimed by OTHER phases (not this one)
    const takenIds = new Set(
      phases
        .filter(p => p.id !== excludePhaseId)
        .flatMap(p => p.courseIds)
    );
    const pickable = availableCourses.filter(c => !takenIds.has(c._id));

    return (
    <div>
      <label className="block text-sm font-medium text-text mb-1.5">Courses</label>
      <div className="border border-line rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={onToggleOpen}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-shell hover:bg-surface transition-colors text-sm text-left"
        >
          <span className={selectedIds.length === 0 ? 'text-muted' : 'text-text'}>
            {selectedIds.length === 0
              ? 'Select courses…'
              : `${selectedIds.length} course${selectedIds.length !== 1 ? 's' : ''} selected`}
          </span>
          <svg className={`w-4 h-4 text-muted transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {open && (
          <div className="border-t border-line max-h-48 overflow-y-auto">
            {pickable.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted">No published courses available.</p>
            ) : pickable.map(course => (
              <label
                key={course._id}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-surface cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(course._id)}
                  onChange={() => onToggle(course._id)}
                  className="w-4 h-4 rounded border-line text-chart focus:ring-chart/30"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text truncate">{course.title}</p>
                  <p className="text-xs text-muted">{course.category}</p>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedIds.map(id => {
            const c = availableCourses.find(x => x._id === id);
            return c ? (
              <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-chart/10 text-chart text-xs font-medium">
                {c.title}
                <button type="button" onClick={() => onToggle(id)} className="hover:text-danger ml-0.5">×</button>
              </span>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
  };

  return (
    <div className="section-gap">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-text">Phase Configuration</h1>
          <p className="text-muted">Configure and reorder program phases</p>
        </div>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={openCreateModal}>+ Add Phase</Button>
          {phases.length > 0 && (
            <Button variant="primary" onClick={handleSaveChanges} disabled={saving}>
              {saving ? 'Saving...' : 'Save Order'}
            </Button>
          )}
        </div>
      </div>

      {/* Phase Cards */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
        </div>
      ) : phases.length === 0 ? (
        <EmptyState
          title="No phases configured"
          description="Click '+ Add Phase' to create your first phase."
          action={<Button variant="primary" onClick={openCreateModal}>+ Add Phase</Button>}
        />
      ) : (
        <div className="space-y-4">
          {phases.map((phase, index) => (
            <div key={phase.id} className="card flex items-start gap-4">
              {/* Order number */}
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-chart flex items-center justify-center text-white font-bold text-sm">
                {phase.order}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-text font-semibold text-lg">{phase.title}</h3>
                  <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-info/20 text-info">
                    Coursework
                  </span>
                </div>
                <p className="text-muted text-sm">{phase.description}</p>

                {phase.courseIds && phase.courseIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {phase.courseIds.map(id => {
                      const c = availableCourses.find(x => x._id === id);
                      return (
                        <span key={id} className="px-2 py-0.5 rounded-full bg-chart/10 text-chart text-xs font-medium">
                          {c ? c.title : id}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => movePhase(index, 'up')}
                  disabled={index === 0}
                  className="p-1.5 rounded-lg text-muted hover:text-text hover:bg-shell transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Move up"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => movePhase(index, 'down')}
                  disabled={index === phases.length - 1}
                  className="p-1.5 rounded-lg text-muted hover:text-text hover:bg-shell transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Move down"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <Button variant="ghost" size="sm" onClick={() => openEditModal(phase)}>Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => handleDeletePhase(phase.id)}
                  className="!text-danger hover:!text-danger/70">
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <Modal isOpen={editModalOpen} onClose={handleCloseEditModal} title="Edit Phase">
        <div className="space-y-4">
          <Input
            label="Title"
            placeholder="Phase title"
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            required
          />
          <Textarea
            label="Description"
            placeholder="Describe this phase..."
            value={editDescription}
            onChange={e => setEditDescription(e.target.value)}
            rows={3}
          />
          <CoursePicker
            selectedIds={editSelectedCourseIds}
            onToggle={toggleEditCourse}
            open={editCoursePickerOpen}
            onToggleOpen={() => setEditCoursePickerOpen(p => !p)}
            excludePhaseId={editingPhase?.id}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={handleCloseEditModal}>Cancel</Button>
            <Button variant="primary" onClick={handleSavePhaseEdit} disabled={!editTitle.trim()}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* Create Modal */}
      <Modal isOpen={createModalOpen} onClose={() => setCreateModalOpen(false)} title="New Phase">
        <div className="space-y-4">
          <Input
            label="Title"
            placeholder="Phase title"
            value={createTitle}
            onChange={e => setCreateTitle(e.target.value)}
            required
          />
          <Textarea
            label="Description"
            placeholder="Describe this phase..."
            value={createDescription}
            onChange={e => setCreateDescription(e.target.value)}
            rows={3}
          />
          <CoursePicker
            selectedIds={createSelectedCourseIds}
            onToggle={toggleCreateCourse}
            open={createCoursePickerOpen}
            onToggleOpen={() => setCreateCoursePickerOpen(p => !p)}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setCreateModalOpen(false)} disabled={creating}>Cancel</Button>
            <Button variant="primary" onClick={handleCreatePhase} disabled={!createTitle.trim() || creating}>
              {creating ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PhaseConfig;
